import classes from "../HeaterSelectionAssistant.module.less";
import { Controller, useForm } from "react-hook-form";
import Radio from "../../common/components/Radio";
import CustomNumberFormat from "../../common/components/CustomNumberFormat";
import { CloseBtn } from "../../AddToCart/components/addToCart/AtcCloseButton";
import { Icon } from "../../common/components/icon/Icon";
import { IconType } from "../../common/components/icon/IconType";
import { LoadingButton } from "../../common/components/Button/Button";
import { useEffect, useState } from "react";
import { getHeaterSelection } from "~/HeaterSelectionAssistant/api/getHeaterSelection";
import { type ResultsHeaterProps } from "~/HeaterSelectionAssistant/HeaterSelectionAssistant";
import PreviewTableMaterialIdInput from "~/BulkATCPopup/components/PreviewTable/PreviewTableMaterialIdInput";
import { type ProductData } from "~/models/ProductData";
import { useIsMinDesktopSize, useIsMinTabletSize } from "~/common/hooks/useMediaQuery";
import { trackHeaterSelection } from "~/feature/Analytics/ga/ga";
import { useTranslation } from "react-i18next";
import { Alert } from "~/common/m3/Alert/Alert";

interface FormProps {
  handleChange: (values: ResultsHeaterProps[] | null) => void;
}

interface HeaterSelectionAssistantForm {
  designation: ProductData | null;
  isMM: boolean;
  hsa_id: number | null;
  hsa_od: number | null;
  hsa_w: number | null;
  isKg: boolean;
  hsa_m: number | null;
}

export function FormHS({ handleChange }: FormProps) {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [isDesignation, setIsDesignation] = useState(false);
  const isMinDesktop = useIsMinDesktopSize();
  const isMinTablet = useIsMinTabletSize();

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<HeaterSelectionAssistantForm>({
    defaultValues: {
      designation: null,
      isMM: true,
      hsa_id: null,
      hsa_od: null,
      hsa_w: null,
      isKg: true,
      hsa_m: null,
    },
    mode: "onChange",
  });

  const designation = watch("designation");
  const innerDiameter = watch("hsa_id");
  const outsideDiameter = watch("hsa_od");
  const width = watch("hsa_w");
  const mass = watch("hsa_m");

  const errorDiameter = innerDiameter && outsideDiameter ? innerDiameter >= outsideDiameter : false;

  const isApplyDisabled = isDesignation
    ? !designation
    : !(!!innerDiameter && !!outsideDiameter && !!width && !!mass) || errorDiameter;

  const resetForm = () => {
    reset();
    handleChange(null);
    isDesignation && setIsDesignation(false);
  };

  useEffect(() => {
    if (!isDesignation) {
      errors?.designation && clearErrors("designation");
    }
  }, [isDesignation]);

  const onSubmitHandler = handleSubmit(async (values) => {
    handleChange(null);
    setIsLoading(true);
    if (!isApplyDisabled) {
      const unit = values.isMM ? "mm" : "inch";
      const unitMass = values.isKg ? "kg" : "lbs";
      const props = {
        code: isDesignation ? values?.designation?.code ?? "" : "",
        attributes: !isDesignation
          ? [
              {
                code: "hsa_id",
                unit: unit,
                value: values.hsa_id,
              },
              {
                code: "hsa_od",
                unit: unit,
                value: values.hsa_od,
              },
              {
                code: "hsa_w",
                unit: unit,
                value: values.hsa_w,
              },
              {
                code: "hsa_m",
                unit: unitMass,
                value: values.hsa_m,
              },
            ]
          : [],
      };
      const attributes = {
        length_unit: unit,
        mass_unit: unitMass,
        inside_diameter: values.hsa_id,
        outside_diameter: values.hsa_od,
        width: values.hsa_w,
        mass: values.hsa_m,
      };
      const result: ResultsHeaterProps[] | null = await getHeaterSelection(props);
      if (result) {
        handleChange(result);
        trackHeaterSelection(isDesignation, values?.designation?.designation ?? "", attributes);
      }
    }
    setIsLoading(false);
  });

  const controllerWeightRadio = () => {
    return (
      <Controller
        control={control}
        name={"isKg"}
        render={({ field: { value, onChange } }) => (
          <div className={"scha__radio-switch__options"}>
            <div className={"scha__radio-switch__option"} onClick={() => !isDesignation && onChange(true)}>
              <Radio active={value === true} className={"icon-margin"} disabled={isDesignation} />
              <span className={isDesignation ? classes.formItemSpanDisabled : classes.formItemSpan}>
                {t("heaterSelectionAssistant.form.radio.kg")}
              </span>
            </div>
            <div className={"scha__radio-switch__option"} onClick={() => !isDesignation && onChange(false)}>
              <Radio active={value === false} className={"icon-margin"} disabled={isDesignation} />
              <span className={isDesignation ? classes.formItemSpanDisabled : classes.formItemSpan}>
                {t("heaterSelectionAssistant.form.radio.lbs")}
              </span>
            </div>
          </div>
        )}
      />
    );
  };

  return (
    <div className={classes.form}>
      <div className={classes.selectLabel}>{t("heaterSelectionAssistant.form.selectLabel")}</div>
      <div className={classes.formRow}>
        <div className={classes.formColumn}>
          <div className={classes.formRadioMain}>
            <div
              className={"scha__radio-switch__option"}
              onClick={() => setIsDesignation(true)}
              tracking-aux-bearing-switch="designation"
            >
              <Radio active={isDesignation} className={"icon-margin"} />
              <span>{t("heaterSelectionAssistant.form.radio.designation")}</span>
            </div>
          </div>
          <Controller
            control={control}
            name={"designation"}
            rules={{
              required: isDesignation,
              validate: isDesignation
                ? (value) => !!value?.designation || `${t("heaterSelectionAssistant.form.input.errorProduct")}`
                : undefined,
            }}
            render={({ field: { value, onChange, onBlur }, fieldState: { error } }) => (
              <div className={classes.formItemInput}>
                <PreviewTableMaterialIdInput
                  onChange={(data) => {
                    if (!data?.designation) {
                      onChange({ code: undefined });
                    }
                    onChange(data);
                  }}
                  onBlur={onBlur}
                  value={value?.designation ? value.designation : ""}
                  label={t("heaterSelectionAssistant.form.input.designation")}
                  data-test-id={""}
                  helperText={error?.message}
                  error={!!error?.message}
                  disabled={!isDesignation}
                />
              </div>
            )}
          />
        </div>
        <div className={classes.formColumn}>
          <div className={classes.formRadioMain}>
            <div
              className={"scha__radio-switch__option"}
              onClick={() => setIsDesignation(false)}
              tracking-aux-bearing-switch="attributes"
            >
              <Radio active={!isDesignation} className={"icon-margin"} />
              <span>{t("heaterSelectionAssistant.form.radio.attributes")}</span>
            </div>
          </div>

          <div className={classes.sectionImageInputs}>
            <div className={classes.divImageInputs}>
              <img
                className={classes.formItemImage}
                style={{ opacity: isDesignation ? 0.5 : 1 }}
                src="https://cdn.schaeffler-ecommerce.com/cdn/001A3966_s04_3c_0k_0k.png"
                alt=""
              />

              {!isMinDesktop && isMinTablet && (
                <div className={classes.sectionMobile}>
                  {controllerWeightRadio()}
                  <div className={classes.formRow}>
                    <Controller
                      control={control}
                      name={"hsa_m"}
                      rules={{
                        required: !isDesignation,
                        validate: !isDesignation
                          ? (value) =>
                              (value && value > 0) || `${t("heaterSelectionAssistant.form.input.error.greaterThan")}`
                          : undefined,
                      }}
                      render={({ field: { value, onChange }, fieldState: { error } }) => (
                        <div className={classes.formItem}>
                          <CustomNumberFormat
                            className={classes.formItemInput}
                            label={t("heaterSelectionAssistant.form.input.mass.label")}
                            value={value}
                            handleState={(e) => onChange(e.floatValue)}
                            error={!!error && !isDesignation}
                            helperText={error?.message}
                            disabled={isDesignation}
                          />
                        </div>
                      )}
                    />
                  </div>
                </div>
              )}
            </div>

            <div className={classes.section}>
              <Controller
                control={control}
                name={"isMM"}
                render={({ field: { value, onChange } }) => (
                  <div className={"scha__radio-switch__options"}>
                    <div className={"scha__radio-switch__option"} onClick={() => !isDesignation && onChange(true)}>
                      <Radio active={value === true} className={"icon-margin"} disabled={isDesignation} />
                      <span className={isDesignation ? classes.formItemSpanDisabled : classes.formItemSpan}>
                        {t("heaterSelectionAssistant.form.radio.length.mm")}
                      </span>
                    </div>
                    <div className={"scha__radio-switch__option"} onClick={() => !isDesignation && onChange(false)}>
                      <Radio active={value === false} className={"icon-margin"} disabled={isDesignation} />
                      <span className={isDesignation ? classes.formItemSpanDisabled : classes.formItemSpan}>
                        {t("heaterSelectionAssistant.form.radio.length.inch")}
                      </span>
                    </div>
                  </div>
                )}
              />

              <div className={classes.formRow}>
                <Controller
                  control={control}
                  name={"hsa_id"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) =>
                          (value && value > 0) || `${t("heaterSelectionAssistant.form.input.error.greaterThan")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div className={classes.formItem}>
                      <CustomNumberFormat
                        className={classes.formItemInput}
                        label={t("heaterSelectionAssistant.form.input.insideDiameter.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue?.toString())}
                        error={!!error && !isDesignation}
                        helperText={error?.message}
                        disabled={isDesignation}
                      />
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={"hsa_od"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) => (value && value > 0) || `${t("bearingFrequencyCalculator.error.numeric")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div className={classes.formItem}>
                      <CustomNumberFormat
                        className={classes.formItemInput}
                        label={t("heaterSelectionAssistant.form.input.outsideDiameter.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue)}
                        error={(!!error || errorDiameter) && !isDesignation}
                        helperText={
                          !errorDiameter
                            ? error?.message
                            : t("heaterSelectionAssistant.form.input.error.outsideDiameter")
                        }
                        disabled={isDesignation}
                      />
                    </div>
                  )}
                />

                <Controller
                  control={control}
                  name={"hsa_w"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) => (value && value > 0) || `${t("bearingFrequencyCalculator.error.numeric")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div className={classes.formItem}>
                      <CustomNumberFormat
                        className={classes.formItemInput}
                        label={t("heaterSelectionAssistant.form.input.width.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue)}
                        error={!!error && !isDesignation}
                        helperText={error?.message}
                        disabled={isDesignation}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          </div>

          {(isMinDesktop || !isMinTablet) && (
            <div className={classes.section}>
              {controllerWeightRadio()}

              <div className={classes.formRow}>
                <Controller
                  control={control}
                  name={"hsa_m"}
                  rules={{
                    required: !isDesignation,
                    validate: !isDesignation
                      ? (value) =>
                          (value && value > 0) || `${t("heaterSelectionAssistant.form.input.error.greaterThan")}`
                      : undefined,
                  }}
                  render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <div className={classes.formItem}>
                      <CustomNumberFormat
                        className={classes.formItemInput}
                        label={t("heaterSelectionAssistant.form.input.mass.label")}
                        value={value}
                        handleState={(e) => onChange(e.floatValue)}
                        error={!!error && !isDesignation}
                        helperText={error?.message}
                        disabled={isDesignation}
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className={classes.formButtonSection}>
        <CloseBtn data-test-id="" onClick={() => resetForm()}>
          <Icon type={IconType.Reset} className="icon-margin" />
          {t("heaterSelectionAssistant.form.resetAll")}
        </CloseBtn>

        <LoadingButton
          data-test-id=""
          onClick={() => onSubmitHandler()}
          disabled={isApplyDisabled}
          inFlight={isLoading}
        >
          {t("heaterSelectionAssistant.form.apply")}
        </LoadingButton>
      </div>

      <Alert severity="info">{t("heaterSelectionAssistant.form.blueMessage")}</Alert>
    </div>
  );
}
